import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CiLogout } from "react-icons/ci";
import { PiTrash, PiCaretDown, PiChartLineUp, PiQuestion, PiShieldCheck, PiLock, PiEyeSlash, PiListChecks, PiGearSix } from "react-icons/pi";
import { post, clearUserSession } from "../utils/api";
import { captureError } from "../utils/errorHandler";
import { isBlockedEfileStatus, setEfileStatus, getDeleteLockReason } from "../utils/onboardingRoutes";
import toast, { toastConfig } from "react-simple-toasts";
import { track } from "../utils/analytics";
import FilingClosureBanner from "./FilingClosureBanner";

toastConfig({ position: "top-right" });

const LayoutOnboarding = ({ children }) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasNoData, setHasNoData] = useState(false);
  const [lockReason, setLockReason] = useState(() => getDeleteLockReason());
  const efileLocked = lockReason !== null;
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const dropdownRef = useRef(null);

  // Get user email from localStorage
  const userEmail = localStorage.getItem("userEmail") || "";
  const userInitial = userEmail ? userEmail.charAt(0).toUpperCase() : "U";
  const userLabel = userEmail
    ? (userEmail.split("@")[0] === "rishabh+jain" ? "User" : userEmail.split("@")[0])
    : "";
  const investingWhitelisted = localStorage.getItem("isWhitelistedUser") === "true";

  // Set page title for onboarding flows
  useEffect(() => {
    document.title = "Drafting tax return - Prime Meridian";
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowDropdown(false);
    post("/auth/sign-out/", {})
      .then((response) => {
        const message = response.data?.message || "Signed out successfully";
        toast(message, { theme: "success" });
        clearUserSession();
        navigate("/login");
      })
      .catch((error) => {
        captureError(error, "logging out");
        const errorMessage =
          error.response?.data?.error || "Logout failed. Please try again.";
        toast(errorMessage, { theme: "failure" });
        // Even if API fails, clear local data and redirect.
        clearUserSession();
        navigate("/login");
      });
  };

  const handleDeleteClick = () => {
    setShowDropdown(false);
    setLockReason(getDeleteLockReason());

    // Check from localStorage if user has any files (set by upload pages)
    const onboardingStep = localStorage.getItem("onboardingStep");
    const fileCount = parseInt(localStorage.getItem("onboardingFileCount") || "0", 10);
    
    // No data if on step 1 and no files uploaded
    const noData = onboardingStep === "1" && fileCount === 0;
    setHasNoData(noData);
    
    setShowDeleteConfirm(true);
    track("onboarding_delete_my_data_confirm_shown");
  };

  const handleConfirmDelete = async () => {
    if (efileLocked) {
      return;
    }

    setIsDeleting(true);
    track("onboarding_delete_my_data_confirmed");

    try {
      await post("/delete-my-data/");
      toast("Your data has been deleted successfully! Redirecting...", { theme: "success" });
      track("onboarding_delete_my_data_success");

      // Clear all localStorage except auth token, email, whitelisted status, and TOS acceptance
      const authToken = localStorage.getItem("token");
      const userEmailStored = localStorage.getItem("userEmail");
      const isWhitelistedUser = localStorage.getItem("isWhitelistedUser");
      const tosAccepted = localStorage.getItem("tosAccepted");
      localStorage.clear();
      if (authToken) {
        localStorage.setItem("token", authToken);
      }
      if (userEmailStored) {
        localStorage.setItem("userEmail", userEmailStored);
      }
      if (isWhitelistedUser) {
        localStorage.setItem("isWhitelistedUser", isWhitelistedUser);
      }
      if (tosAccepted) {
        localStorage.setItem("tosAccepted", tosAccepted);
      }

      // Wait 2 seconds for user to see toast, then force full page reload to reset all component state
      setTimeout(() => {
        window.location.href = "/onboarding/upload";
      }, 2000);
    } catch (error) {
      captureError(error, "delete my data from onboarding");
      const statusCode = error?.response?.status;
      const efileStatusFromError = error?.response?.data?.efile_status;
      const isEfileConflict =
        statusCode === 409 || isBlockedEfileStatus(efileStatusFromError);

      if (isEfileConflict) {
        setEfileStatus(efileStatusFromError || "efile_successful");
        setLockReason(getDeleteLockReason());
        setDeleteConfirmText("");
        track("onboarding_delete_my_data_blocked_efile_successful");
      } else {
        track("onboarding_delete_my_data_error");
      }

      const errorMsg = error.response?.data?.error || "Failed to delete data. Please try again.";
      toast(errorMsg, { theme: "failure" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText("");
    track("onboarding_delete_my_data_confirm_cancelled");
  };

  // Dropdown Menu Component
  const AccountDropdown = ({ isMobile = false }) => (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-1.5 md:gap-2 hover:bg-[#F5F5F5] rounded-lg px-2 py-1.5 transition-colors cursor-pointer"
      >
        {/* Avatar */}
        <div className="w-7 h-7 rounded-full bg-black flex items-center justify-center">
          <span className="text-white text-xs font-medium">{userInitial}</span>
        </div>
        {/* Username - hide on mobile */}
        {!isMobile && userEmail && (
          <span className="text-sm text-[#1c1c1c] font-medium max-w-[120px] truncate">
            {userLabel}
          </span>
        )}
        <PiCaretDown className={`w-3.5 h-3.5 text-[#717171] transition-transform ${showDropdown ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div 
          className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-[#E5E5E5] py-2 z-50"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Account Info */}
          {userEmail && (
            <>
              <div className="px-4 py-2">
                <p className="text-sm font-medium text-[#1c1c1c]">Account</p>
                <p className="text-xs text-[#717171] truncate">{userEmail}</p>
              </div>
              <div className="border-t border-[#E5E5E5] my-1" />
            </>
          )}

          {investingWhitelisted && (
            <>
              <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate("/investing");
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
              >
                <PiChartLineUp className="w-4 h-4" />
                Switch to Investing
              </button>
              <div className="border-t border-[#E5E5E5] my-1" />
            </>
          )}

          {/* Settings (two-step verification + password) */}
          <button
            onClick={() => {
              setShowDropdown(false);
              navigate("/setting");
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
          >
            <PiGearSix className="w-4 h-4" />
            Settings
          </button>

          {/* Help */}
          <a
            href="mailto:help@getprimemeridian.com"
            onClick={() => setShowDropdown(false)}
            className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
          >
            <PiQuestion className="w-4 h-4" />
            Help
          </a>

          {/* Sign Out */}
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
          >
            <CiLogout className="w-4 h-4" />
            Sign out
          </button>

          <div className="border-t border-[#E5E5E5] my-1" />

          {/* Delete My Data */}
          <button
            onClick={handleDeleteClick}
            className="w-full px-4 py-2.5 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2] transition-colors flex items-center gap-2 cursor-pointer"
          >
            <PiTrash className="w-4 h-4" />
            Delete my data
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="onboard-layout">
      <header className="border-b border-[#E5E5E5] shrink-0">
        {/* Desktop Header */}
        <div className="onboard-header-pc relative mx-auto py-6 px-8 lg:px-16">
          <div className="flex items-center">
            <p className="font-lora font-semibold text-lg text-black">
              Prime Meridian
            </p>
          </div>

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <h1 className="text-[20px] font-semibold text-[#717171]">
              Drafting tax return
            </h1>
          </div>

          {/* Desktop Account Dropdown */}
          <div className="absolute right-8 lg:right-16 top-1/2 -translate-y-1/2">
            <AccountDropdown />
          </div>
        </div>

        {/* Mobile Header */}
        <div className="onboard-header-sp flex items-center justify-between pt-[20px] pb-[15px] px-4">
          <div>
            <p className="font-lora font-semibold text-base text-black">
              Prime Meridian
            </p>
            <p className="font-lora font-semibold text-sm text-[#717171]">
              Drafting tax return
            </p>
          </div>
          {/* Mobile Account Dropdown */}
          <AccountDropdown isMobile />
        </div>
      </header>

      <FilingClosureBanner />

      {/* E-filing Delay Alert Banner
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-16 py-3">
          <div className="flex items-center gap-2 text-amber-800">
            <span className="text-lg flex-shrink-0">⚠️</span>
            <p className="text-sm font-medium">
              E-filing is suffering delays due to excessive demand. Please expect an update later this week.
            </p>
          </div>
        </div>
      </div>
      */}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-sm w-full">
            {efileLocked ? (
              <>
                <h2 className="font-semibold text-xl text-[#1c1c1c] mb-4">
                  Data Deletion Unavailable
                </h2>
                <p className="text-sm text-[#717171] mb-6">
                  {lockReason === 'max_retries' ? (
                    <>Due to IRS guidelines, we cannot allow deleting your data due to repeated identity verification failures. Your data is stored solely to stop more users from attempting to use this identity on Prime Meridian.</>
                  ) : lockReason === 'efile' ? (
                    <>Your return has been successfully filed, so data deletion is disabled in accordance with IRS instructions for Form 8879. To request an exception, please email{" "}
                    <a href="mailto:help@getprimemeridian.com" className="text-[#8C6222] underline">
                      help@getprimemeridian.com
                    </a>.</>
                  ) : (
                    <>We cannot delete your data at this point of time as your tax return is currently being processed. To request an exception, please email{" "}
                    <a href="mailto:help@getprimemeridian.com" className="text-[#8C6222] underline">
                      help@getprimemeridian.com
                    </a>.</>
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="w-full tracking-wide text-[#1c1c1c] font-medium text-base rounded-lg py-3 text-center bg-[#E5E5E5] hover:bg-[#D4D4D4] transition-colors cursor-pointer"
                >
                  Close
                </button>
              </>
            ) : hasNoData ? (
              <>
                <h2 className="font-semibold text-xl text-[#1c1c1c] mb-4">
                  No data found
                </h2>
                <p className="text-sm text-[#717171] mb-4">
                  There is no data currently stored for you.
                </p>
                <p className="text-sm text-[#717171] mb-6 italic">
                  Your data is never sold, and is never used for AI model training anywhere. Your data is stored on Prime Meridian servers only for your own visibility, and can be deleted at any time by pressing this button.
                </p>
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="w-full tracking-wide text-white font-medium text-base rounded-lg py-3 text-center bg-black hover:bg-[#8C6222] transition-colors cursor-pointer"
                >
                  Okay
                </button>
              </>
            ) : (
              <>
                <h2 className="font-semibold text-xl text-[#1c1c1c] mb-4">
                  Permanently delete all your data?
                </h2>
                <p className="text-sm text-[#717171] mb-4">
                  This action cannot be undone.
                </p>
                <p className="text-sm text-[#717171] mb-4">
                  Prime Meridian will <b>NOT</b> file your 2025 tax return if you delete your data now.
                </p>
                <p className="text-sm text-[#717171] mb-4 italic">
                  Your data is never sold, and is never used for AI model training anywhere. Your data is stored on Prime Meridian servers only for your own visibility, and can be deleted at any time by pressing this button.
                </p>
                
                {/* Confirmation text input */}
                <div className="mb-6">
                  <label className="block text-sm text-[#717171] mb-2">
                    To proceed, please type <span className="font-semibold text-[#1c1c1c]">confirm delete</span>
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="confirm delete"
                    disabled={isDeleting}
                    className="w-full px-4 py-2.5 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#DC2626] transition-colors disabled:opacity-50"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                    className="flex-1 tracking-wide text-[#1c1c1c] font-medium text-base rounded-lg py-3 text-center bg-[#E5E5E5] hover:bg-[#D4D4D4] transition-colors cursor-pointer disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting || deleteConfirmText !== "confirm delete"}
                    className={`flex-1 tracking-wide text-white font-medium text-base rounded-lg py-3 text-center transition-colors disabled:opacity-50 ${
                      deleteConfirmText === "confirm delete" 
                        ? "bg-[#DC2626] hover:bg-[#B91C1C] cursor-pointer" 
                        : "bg-[#DC2626]/30 cursor-not-allowed"
                    }`}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <main className="flex-1 px-4 md:px-8 lg:px-16 py-6 pb-8 md:py-8 md:pb-12">
      {children}
      
      {/* Security Trust Section */}
      <div className="w-full max-w-3xl mx-auto mt-12 md:mt-16">
        <div className="border border-[#E5E5E5] rounded-xl bg-white/60 p-5 md:p-6">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4 md:mb-5">
            <PiShieldCheck className="w-5 h-5 text-[#16A34A]" />
            <span className="text-base font-semibold text-[#1c1c1c]">Your data is protected</span>
          </div>
          
          {/* Grid - 2x2 on mobile, 4 columns on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {/* Bank-Level Encryption */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                <PiLock className="w-4 h-4 text-[#717171]" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#1c1c1c]">Bank-Level Encryption</span>
                <span className="text-xs text-[#717171] leading-relaxed">256-bit AES encryption protects your data in transit and at rest</span>
              </div>
            </div>
            
            {/* Never Sold or Trained */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                <PiEyeSlash className="w-4 h-4 text-[#717171]" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#1c1c1c]">Never Sold or Trained</span>
                <span className="text-xs text-[#717171] leading-relaxed">Your data is never sold, or used for AI model training</span>
              </div>
            </div>
            
            {/* IRS Security Six */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                <PiListChecks className="w-4 h-4 text-[#717171]" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#1c1c1c]">IRS Security Six</span>
                <span className="text-xs text-[#717171] leading-relaxed">All six IRS-recommended safeguards for tax data protection</span>
              </div>
            </div>
            
            {/* Delete Anytime */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                <PiTrash className="w-4 h-4 text-[#717171]" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#1c1c1c]">Delete Anytime</span>
                <span className="text-xs text-[#717171] leading-relaxed">Permanently remove all your data with one click</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </main>
      <footer className="shrink-0 py-4 text-center">
        <p className="text-sm text-[#717171]">
          Having issues?{" "}
          <a 
            href="mailto:help@getprimemeridian.com" 
            className="text-black hover:text-[#8C6222] transition-colors"
          >
            help@getprimemeridian.com
          </a>
        </p>
      </footer>
    </div>
  );
};
export default LayoutOnboarding;
